import { useEffect, useState } from "react";
import api from "../api/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import { useScreen } from "../hooks/useScreen";

const emptyRecipient = { email: "", name: "", is_active: true };

const emptyForm = {
  name: "",
  subject: "",
  description: "",
  is_active: true,
  frequency: "manual",
  schedule_time: "08:00",
  schedule_day_of_week: "",
  schedule_day_of_month: "",
  timezone: "America/Argentina/La_Rioja",
  include_html: true,
  include_pdf: false,
  recipients: [{ ...emptyRecipient }],
};

const weekDays = [
  { value: 0, label: "Lunes" },
  { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" },
  { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];

export default function EmailReports() {
  const { isXs } = useScreen();
  const [reports, setReports] = useState([]);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [runsOpen, setRunsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const normalizeError = (detail, fallback) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(" | ");
    }
    if (detail && typeof detail === "object") {
      return detail.message || JSON.stringify(detail);
    }
    return fallback;
  };

  const showError = (detail, fallback) => setError(normalizeError(detail, fallback));

  const fetchReports = () => {
    api
      .get("/email-reports/")
      .then((res) => setReports(res.data))
      .catch((err) => showError(err?.response?.data?.detail, "No se pudieron cargar los reportes"));
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleOpenCreate = () => {
    setEditing(false);
    setSelectedReport(null);
    setFormData(emptyForm);
    setOpen(true);
  };

  const handleOpenEdit = (report) => {
    setEditing(true);
    setSelectedReport(report);
    setFormData({
      name: report.name,
      subject: report.subject,
      description: report.description || "",
      is_active: report.is_active,
      frequency: report.frequency,
      schedule_time: report.schedule_time || "08:00",
      schedule_day_of_week: report.schedule_day_of_week ?? "",
      schedule_day_of_month: report.schedule_day_of_month ?? "",
      timezone: report.timezone,
      include_html: report.include_html,
      include_pdf: report.include_pdf,
      recipients: report.recipients?.length ? report.recipients.map((r) => ({ email: r.email, name: r.name || "", is_active: r.is_active })) : [{ ...emptyRecipient }],
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(false);
    setSelectedReport(null);
    setFormData(emptyForm);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleRecipientChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.map((recipient, currentIndex) =>
        currentIndex === index ? { ...recipient, [field]: value } : recipient
      ),
    }));
  };

  const handleAddRecipient = () => {
    setFormData((prev) => ({ ...prev, recipients: [...prev.recipients, { ...emptyRecipient }] }));
  };

  const handleRemoveRecipient = (index) => {
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const handleSave = () => {
    const payload = {
      ...formData,
      schedule_day_of_week: formData.frequency === "weekly" && formData.schedule_day_of_week !== "" ? Number(formData.schedule_day_of_week) : null,
      schedule_day_of_month: formData.frequency === "monthly" && formData.schedule_day_of_month !== "" ? Number(formData.schedule_day_of_month) : null,
      recipients: formData.recipients.filter((recipient) => recipient.email.trim() !== ""),
    };

    const request = editing
      ? api.put(`/email-reports/${selectedReport.id}`, payload)
      : api.post("/email-reports/", payload);

    request
      .then(() => {
        setSuccessMessage(editing ? "Reporte actualizado correctamente" : "Reporte creado correctamente");
        handleClose();
        fetchReports();
      })
      .catch((err) => showError(err?.response?.data?.detail, "No se pudo guardar el reporte"));
  };

  const handleDelete = (id) => {
    if (!window.confirm("¿Seguro que querés eliminar este reporte?")) return;

    api
      .delete(`/email-reports/${id}`)
      .then(() => {
        setSuccessMessage("Reporte eliminado correctamente");
        fetchReports();
      })
      .catch((err) => showError(err?.response?.data?.detail, "No se pudo eliminar el reporte"));
  };

  const handleTestSend = (report) => {
    api
      .post(`/email-reports/${report.id}/test-send`)
      .then(() => {
        setSuccessMessage("Prueba registrada correctamente");
        fetchReports();
      })
      .catch((err) => showError(err?.response?.data?.detail, "No se pudo registrar la prueba"));
  };

  const handleOpenRuns = (report) => {
    setSelectedReport(report);
    api
      .get(`/email-reports/${report.id}/runs`)
      .then((res) => {
        setRuns(res.data);
        setRunsOpen(true);
      })
      .catch((err) => showError(err?.response?.data?.detail, "No se pudo cargar el historial"));
  };

  const reportColumns = [
    { field: "name", headerName: "Nombre", flex: 1.2 },
    { field: "subject", headerName: "Asunto", flex: 1.6 },
    { field: "frequency", headerName: "Frecuencia", flex: 0.8 },
    {
      field: "is_active",
      headerName: "Activo",
      flex: 0.6,
      valueGetter: (_, row) => (row.is_active ? "Sí" : "No"),
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, py: 1, flexWrap: "wrap" }}>
          <Button size="small" color="warning" onClick={() => handleOpenEdit(params.row)}>
            Editar
          </Button>
          <Button size="small" onClick={() => handleTestSend(params.row)}>
            Enviar prueba
          </Button>
          <Button size="small" onClick={() => handleOpenRuns(params.row)}>
            Historial
          </Button>
          <Button size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            Eliminar
          </Button>
        </Box>
      ),
    },
  ];

  const runColumns = [
    { field: "run_type", headerName: "Tipo", flex: 0.8 },
    { field: "status", headerName: "Estado", flex: 0.8 },
    { field: "subject_snapshot", headerName: "Asunto", flex: 1.4 },
    { field: "recipient_snapshot", headerName: "Destinatarios", flex: 1.6 },
    { field: "created_at", headerName: "Creado", flex: 1.2 },
    { field: "error_message", headerName: "Error", flex: 1.2 },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reporte email
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={handleOpenCreate}>
          Agregar reporte
        </Button>
      </Box>

      <Box sx={{ width: "100%", height: isXs ? 420 : 520 }}>
        <DataGrid
          rows={reports}
          columns={reportColumns}
          disableColumnMenu={isXs}
          density={isXs ? "compact" : "standard"}
          initialState={{ pagination: { paginationModel: { pageSize: isXs ? 5 : 10 } } }}
          pageSizeOptions={[5, 10]}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={open} onClose={handleClose} fullScreen={isXs} fullWidth maxWidth="md">
        <DialogTitle>{editing ? "Editar reporte email" : "Agregar reporte email"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" name="name" value={formData.name} onChange={handleChange} fullWidth />
            <TextField label="Asunto" name="subject" value={formData.subject} onChange={handleChange} fullWidth />
            <TextField label="Descripción" name="description" value={formData.description} onChange={handleChange} multiline minRows={3} fullWidth />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField select label="Frecuencia" name="frequency" value={formData.frequency} onChange={handleChange} fullWidth>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="daily">Diaria</MenuItem>
                <MenuItem value="weekly">Semanal</MenuItem>
                <MenuItem value="monthly">Mensual</MenuItem>
              </TextField>
              <TextField label="Hora" name="schedule_time" type="time" value={formData.schedule_time} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Timezone" name="timezone" value={formData.timezone} onChange={handleChange} fullWidth />
            </Stack>

            {formData.frequency === "weekly" && (
              <TextField select label="Día de la semana" name="schedule_day_of_week" value={formData.schedule_day_of_week} onChange={handleChange} fullWidth>
                {weekDays.map((day) => (
                  <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                ))}
              </TextField>
            )}

            {formData.frequency === "monthly" && (
              <TextField
                label="Día del mes"
                name="schedule_day_of_month"
                type="number"
                value={formData.schedule_day_of_month}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 1, max: 31 }}
              />
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControlLabel control={<Checkbox name="is_active" checked={formData.is_active} onChange={handleChange} />} label="Activo" />
              <FormControlLabel control={<Checkbox name="include_html" checked={formData.include_html} onChange={handleChange} />} label="Enviar HTML" />
              <FormControlLabel control={<Checkbox name="include_pdf" checked={formData.include_pdf} onChange={handleChange} />} label="Generar PDF" />
            </Stack>

            <Typography variant="h6">Destinatarios</Typography>

            {formData.recipients.map((recipient, index) => (
              <Stack key={`${recipient.email}-${index}`} direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                <TextField label="Email" value={recipient.email} onChange={(event) => handleRecipientChange(index, "email", event.target.value)} fullWidth />
                <TextField label="Nombre" value={recipient.name} onChange={(event) => handleRecipientChange(index, "name", event.target.value)} fullWidth />
                <FormControlLabel
                  control={<Checkbox checked={recipient.is_active} onChange={(event) => handleRecipientChange(index, "is_active", event.target.checked)} />}
                  label="Activo"
                />
                <IconButton color="error" onClick={() => handleRemoveRecipient(index)} disabled={formData.recipients.length === 1}>
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}

            <Box>
              <Button onClick={handleAddRecipient}>Agregar destinatario</Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={runsOpen} onClose={() => setRunsOpen(false)} fullScreen={isXs} fullWidth maxWidth="lg">
        <DialogTitle>Historial de envíos{selectedReport ? ` - ${selectedReport.name}` : ""}</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", height: isXs ? 360 : 420, mt: 1 }}>
            <DataGrid
              rows={runs}
              columns={runColumns}
              disableColumnMenu={isXs}
              density={isXs ? "compact" : "standard"}
              pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunsOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(successMessage)} autoHideDuration={3000} onClose={() => setSuccessMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSuccessMessage("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar open={Boolean(error)} autoHideDuration={4000} onClose={() => setError("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setError("")} severity="error" variant="filled" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
